/**
 * ReplacePage — V103-4-5 → V103-10-6: replace ONE component (from the
 * boundary's drift row, e.g. an unverified sub-processor that caps GovRAMP at
 * Provisional). Route: /w/:slug/app/programs/:program/scope/replace/:componentId
 *
 * Shows what changes per program (provider leaving, cap lifted, losses) and
 * adds the milestones as work items (never POA&M items). Nothing in the
 * boundary changes here. The milestones are keyed by component × loss, so a
 * later full move lists them as "Already planned" with these dates
 * (build acceptance #5).
 *
 * Reads: any member (preview). Write: program lead or owner/admin.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Input, PageHeader, Select, Spinner } from '@aigovops/design-system';
import { getProgramManifest } from '@aigovops/core';
import { useWorkspace } from '../../lib/workspace';
import { getToday, listWorkspaceMembers, listProgramExternalServices } from '../../lib/api';
import { queryKeys } from '../../lib/query-keys';
import { getScope, listComponents, planReplacement, previewReplacement } from '../../lib/boundary-api';
import { KIND_LABEL, programLabel, providerLabel } from '../../lib/boundary-model';
import { shortDate } from '../../lib/transition-model';
import '../today.css';
import '../../components/programs/component-boundary.css';

const errMsg = (e: unknown, f: string) => (e instanceof Error && e.message ? e.message : f);

export function ReplacePage() {
  const { program = '', componentId = '' } = useParams<{ program: string; componentId: string }>();
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug, prefix = `/w/${slug}/app`;
  const qc = useQueryClient();
  const m = getProgramManifest(program);
  const back = `${prefix}/programs/${program}/scope?view=issues`;
  const components = useQuery({ queryKey: ['boundary', slug, 'components'], queryFn: () => listComponents(slug, program), enabled: !!m });
  const scope = useQuery({ queryKey: ['boundary', slug, 'scope', program, null], queryFn: () => getScope(slug, program, null), enabled: !!m });
  const services = useQuery({ queryKey: ['transition', slug, program, 'services'], queryFn: () => listProgramExternalServices(slug, program), enabled: !!m });
  const members = useQuery({ queryKey: ['workspace-members', slug], queryFn: () => listWorkspaceMembers(slug), retry: false, staleTime: 60_000 });
  const today = useQuery({ queryKey: queryKeys.today(slug), queryFn: () => getToday(slug), staleTime: 60_000 });
  const all = components.data?.components ?? [];
  const comp = all.find((c) => c.component_id === componentId) ?? null;
  const candidates = all.filter((c) => c.component_id !== componentId).sort((a, b) => Number(b.kind === comp?.kind) - Number(a.kind === comp?.kind) || (a.component_key < b.component_key ? -1 : 1)).slice(0, 8);
  const [choice, setChoice] = useState<string | null>(null);
  useEffect(() => { if (choice === null && candidates[0]) setChoice(candidates[0].component_id); }, [candidates, choice]);
  const keep = choice === 'keep';
  const replacement = keep ? null : choice;
  const preview = useQuery({
    queryKey: ['boundary', slug, 'replacement-preview', componentId, replacement],
    queryFn: () => previewReplacement(slug, program, { component_id: componentId, replacement_component_id: replacement }),
    enabled: !!m && !!comp && choice !== null,
  });
  const [dates, setDates] = useState<Record<string, string>>({});
  const [owners, setOwners] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const names = new Map((services.data?.external_services ?? []).map((s) => [s.external_service_id, s.name]));
  const isAdmin = role === 'owner' || role === 'admin';
  const canWrite = isAdmin || !!today.data?.viewer.lead_program_ids.some((id) => scope.data?.scope.profile_id === id);

  if (!m) return <Navigate to={`${prefix}/programs`} replace />;
  if (components.isLoading) return <div className="gp-program-shell"><Spinner label="Loading the component" /></div>;
  if (!comp) return <div className="gp-program-shell"><EmptyState title="That component is not in the register" action={<Link to={back}>Back to the boundary</Link>} /></div>;
  const p = preview.data?.preview ?? null;
  const toAdd = (p?.milestones ?? []).filter((x) => !x.already_planned);
  const memberName = (id: string | null) => { const x = (members.data?.members ?? []).find((mm) => mm.user_id === id); return x ? x.display_name || x.email : 'Unassigned'; };

  const submit = async () => {
    if (!replacement || busy || !toAdd.length) return;
    setBusy(true); setError(null);
    try {
      const r = await planReplacement(slug, program, {
        component_id: componentId, replacement_component_id: replacement,
        milestones: toAdd.map((x) => ({ key: x.key, due_on: dates[x.key] ?? x.due_on, owner_user_id: owners[x.key] ?? x.owner_user_id })),
      });
      await qc.invalidateQueries({ queryKey: ['boundary', slug] });
      setDone(r.created.length);
      window.setTimeout(() => heading.current?.focus(), 0);
    } catch (e) { setError(errMsg(e, 'The milestones could not be added.')); }
    finally { setBusy(false); }
  };

  return (
    <div className="gp-program-shell gp-tr">
      <nav className="gp-crumbs" aria-label="Breadcrumb">
        <Link to={`${prefix}/programs`}>Programs</Link><span aria-hidden="true">/</span>
        <Link to={back}>{`${programLabel(program)} boundary`}</Link><span aria-hidden="true">/</span>
        <span aria-current="page">{`Replace ${comp.component_key}`}</span>
      </nav>
      <PageHeader variant="app" headingId="rp-h1" title={`Replace ${comp.component_key}`}
        subtitle={`${providerLabel(comp, names)} · ${KIND_LABEL[comp.kind]}${p?.programs.length ? ` · in ${p.programs.map((x) => x.label).join(' + ')}` : ''}`} />
      {error ? <Alert variant="error" onDismiss={() => setError(null)}>{error}</Alert> : null}
      <h2 className="sr-only" ref={heading} tabIndex={-1}>{done !== null ? 'Milestones added' : 'Replace with'}</h2>
      {done !== null ? (
        <Alert variant="success"><p><b>{`${done} milestone${done === 1 ? '' : 's'} added.`}</b> A later government-cloud move lists them as already planned, with these dates.</p>
          <Link to={back}>Back to the boundary</Link></Alert>
      ) : null}

      <Card>
        <fieldset className="gp-tr-prov">
          <legend className="gp-card-h">Replace with</legend>
          {candidates.length === 0 ? <EmptyState title="No other component can take over yet" action={<Link to={`${prefix}/programs/${program}/scope`}>Add a component</Link>} /> : null}
          {candidates.map((c) => (
            <label key={c.component_id} className="gp-tr-choice gp-tr-tile">
              <input type="radio" name="replacement" checked={choice === c.component_id} onChange={() => setChoice(c.component_id)} />
              <span className="gp-tr-choice-t"><b>{c.component_key}</b><span className="gp-dim">{`${KIND_LABEL[c.kind]} · ${providerLabel(c, names)}`}</span></span>
            </label>
          ))}
          <label className="gp-tr-choice gp-tr-tile">
            <input type="radio" name="replacement" checked={keep} onChange={() => setChoice('keep')} />
            <span className="gp-tr-choice-t"><b>{`Keep ${comp.component_key}`}</b><span className="gp-dim">Add a GovRAMP or FedRAMP verification instead</span></span>
          </label>
        </fieldset>

        <h2 className="gp-card-h" id="wc-h">What changes</h2>
        {preview.isLoading ? <Spinner label="Working out what changes" /> : p ? (
          <ul className="gp-cb-rows" aria-labelledby="wc-h">
            {p.programs.map((x) => (
              <li key={x.profile_id} className="gp-cb-row">
                <span className="gp-cb-main"><b>{x.label}</b>
                  <span className="gp-cb-meta">
                    {keep ? <span>{`${comp.component_key} stays`}</span> : x.provider_removed ? <span>{`${x.provider_removed.name} leaves the boundary`}</span> : <span>No third party leaves</span>}
                    {x.lost.length ? <span>{`${x.lost.map((l) => l.label).join(', ')} becomes yours`}</span> : null}
                  </span>
                </span>
                {x.cap === 'lifted' ? <span className="gp-cb-chip" data-tone="ok"><span aria-hidden="true">✓ </span>Provisional cap lifted</span>
                  : x.cap === 'added' ? <span className="gp-cb-chip" data-tone="bad"><span aria-hidden="true">✕ </span>Caps GovRAMP at Provisional</span>
                  : <span className="gp-cb-chip">{x.provider_removed || x.lost.length ? 'Changes' : 'No change'}</span>}
              </li>
            ))}
          </ul>
        ) : null}

        {keep ? (
          <div className="gp-actions"><Link className="gp-linkbtn" data-variant="primary" to={back}>Add verification</Link><Link to={back}>Cancel</Link></div>
        ) : p ? <>
          <h2 className="gp-card-h" id="gm-h">{`Milestones · ${p.milestones.length}`}</h2>
          <p className="gp-dim">Planned-change work · not POA&amp;M items</p>
          <table className="gp-table gp-tr-table" role="table" aria-labelledby="gm-h">
            <thead role="rowgroup"><tr role="row"><th role="columnheader" scope="col">Milestone</th><th role="columnheader" scope="col">Owner</th><th role="columnheader" scope="col">Date</th></tr></thead>
            <tbody role="rowgroup">
              {p.milestones.map((x) => (
                <tr role="row" key={x.key}>
                  <th role="rowheader" scope="row" className="gp-tr-ms"><b>{x.title}</b> {x.already_planned ? <span className="gp-cb-chip" data-tone="info">Already planned</span> : null}</th>
                  <td role="cell" data-label="Owner">
                    {x.already_planned || !canWrite ? memberName(x.owner_user_id) : (
                      <Select aria-label={`Owner, ${x.title}`} value={owners[x.key] ?? x.owner_user_id ?? ''} onChange={(e) => setOwners((o) => ({ ...o, [x.key]: e.currentTarget.value }))}>
                        {(members.data?.members ?? []).map((mm) => <option key={mm.user_id} value={mm.user_id}>{mm.display_name || mm.email}</option>)}
                      </Select>
                    )}
                  </td>
                  <td role="cell" data-label="Date">
                    {x.already_planned ? shortDate(x.already_planned.due_at) : !canWrite ? shortDate(x.due_on) : (
                      <Input type="date" aria-label={`Date, ${x.title}`} value={dates[x.key] ?? x.due_on} onChange={(e) => setDates((d) => ({ ...d, [x.key]: e.currentTarget.value }))} />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="gp-actions">
            {canWrite ? <Button variant="primary" aria-disabled={busy || !toAdd.length || undefined} onClick={() => void submit()}>{toAdd.length ? `Add ${toAdd.length} milestone${toAdd.length === 1 ? '' : 's'}` : 'Every milestone is already planned'}</Button>
              : <span className="gp-dim">The program lead or an admin adds the milestones.</span>}
            <Link className="gp-linkbtn" data-variant="ghost" to={back}>Cancel</Link>
            <Link to={`${prefix}/programs/${program}/scope/move`}>Plan a full government cloud move instead</Link>
          </div>
        </> : null}
      </Card>
    </div>
  );
}
